import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    GetMcfxForms as GetMcfxFormsQuery,
    GetMcfxFormsVariables as GetMcfxFormsQueryVariables,
    McfxFormFragment as McfxForm,
} from 'nutshell-graphql-types';

import GET_MCFX_FORMS from './queries/get-mcfx-forms.graphql';

type Props = {
    queryVariables: GetMcfxFormsQueryVariables;
    limit: number;
    children: (arg1: {
        mcfxForms: McfxForm[];
        totalCount: number;
        error: string | null | undefined;
        isLoading: boolean;
        refetch: () => Promise<any>;
        fetchMoreRows: () => Promise<any>;
        hasNextPage: boolean;
    }) => React.ReactElement;
};

export function McfxFormListQuery(props: Props) {
    const {
        data,
        loading: isLoading,
        error,
        refetch,
        fetchMore,
    } = useQuery<GetMcfxFormsQuery, Record<any, any>>(GET_MCFX_FORMS, {
        variables: {...props.queryVariables, limit: props.limit},
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    const mcfxFormsData = data ? data.mcfxForms : null;

    const mcfxForms = mcfxFormsData
        ? mcfxFormsData.edges.map((form) => {
              return form.node;
          })
        : [];

    const totalCount = mcfxFormsData ? mcfxFormsData.totalCount : 0;

    const fetchMoreRows = () => {
        return fetchMore({
            variables: {
                first: props.limit,
                after: mcfxFormsData ? mcfxFormsData.pageInfo.endCursor : null,
            },
            // @ts-expect-error
            updateQuery: (
                prev: GetMcfxFormsQuery,
                newResults: {
                    fetchMoreResult: GetMcfxFormsQuery;
                }
            ) => {
                const oldForms = prev.mcfxForms;
                const fetchedForms = newResults.fetchMoreResult.mcfxForms;

                const newForms = {
                    ...oldForms,
                    pageInfo: fetchedForms.pageInfo,
                    edges: oldForms.edges.concat(fetchedForms.edges),
                } as const;

                return {mcfxForms: newForms};
            },
        });
    };

    const hasNextPage = mcfxFormsData ? mcfxFormsData.pageInfo.hasNextPage : false;

    return props.children({
        mcfxForms,
        totalCount,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        isLoading,
        refetch,
        fetchMoreRows,
        hasNextPage,
    });
}
